import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import __ from "src/utils/Translations";
import { getEmployeesOptionsFetchMethod } from "src/Components/FormElements/Autocomplete/commonFetchMethods";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from "src/utils/constants/api";

export const EmployeesAutocomplete = ({
  name,
  label,
}: {
  name: string;
  label: string;
}) => {
  const { control } = useFormContext();

  const companyId = useCompanyId();
  const [search, setSearch] = useState("");

  const fetchFn = getEmployeesOptionsFetchMethod(null, { companyId }, true);

  const { data, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery<{ value: string; label: string }[]>({
      queryKey: ["employees", { companyId, search }],
      queryFn: ({ pageParam }) => fetchFn(search, pageParam as number),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length >= DEFAULT_ITEMS_COUNT_PER_REQUEST) {
          return allPages.length + 1;
        }
        return undefined;
      },
      enabled: !!companyId && search.length >= 1,
      staleTime: 60000,
      placeholderData: keepPreviousData,
    });

  const options = useMemo(
    () => data?.pages.flatMap((page) => page) || [],
    [data?.pages]
  );

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    // Load more when user has scrolled 80% of the way down
    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, ref, value },
        fieldState: { invalid, error },
      }) => (
        <Autocomplete
          options={options}
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, selectedValue) =>
            option.value === selectedValue.value
          }
          value={value ?? null}
          onChange={(__, newValue, reason) => {
            if (reason === "selectOption") {
              onChange(newValue);
            }
            if (reason === "clear") {
              onChange(null);
            }
          }}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === "input") {
              handleSearchChange(newInputValue);
            }
          }}
          loading={isFetching}
          noOptionsText={__(
            search.length > 0 ? "Brak wyników" : "Zacznij wpisywanie"
          )}
          filterOptions={(options) => options}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputRef={ref}
              error={invalid}
              helperText={error?.message}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps?.input,
                  endAdornment: (
                    <>
                      {isFetching ? (
                        <CircularProgress
                          color="inherit"
                          size={20}
                          sx={{ minWidth: "20px" }}
                        />
                      ) : null}
                      {params.slotProps?.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: handleScroll,
              style: {
                maxHeight: "300px",
                overflowY: "auto",
              },
            },
          }}
        />
      )}
    />
  );
};
